import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Accessibility, HandCoins, Users, Wrench, GraduationCap, HeartHandshake } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api } from "@/lib/api";

const HERO = "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/88fff89784896119feb614acac1afbf4e20601909154273967756ddd12206466.jpeg";

const TEMOIGNAGES = [
  {
    img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/9d909d8a7d0adea36528f5c906a940f706075ae87fada6eff3d2e435c8faf4b2.jpeg",
    quote: "Grâce à l'étude de poste et à l'exosquelette lombaire, nous avons pu maintenir en poste un préparateur de commandes en restriction d'aptitude. Dix-huit mois plus tard, il est toujours là — et il forme les nouveaux.",
    name: "Ibrahima Sarr", role: "Responsable RH, plateforme logistique (94)",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/6c8da57929ec0d5192b67e805fe3907743e21a33744461bf7302774350a291a1.jpeg",
    quote: "Mon poste a été entièrement repensé : bureau réglable, périphériques adaptés, accès dégagés. Le dossier AGEFIPH a été monté par ALDOL-SOLUTIONS en trois semaines. Je travaille enfin sans compenser en permanence.",
    name: "Claire Moreau", role: "Chargée de clientèle, utilisatrice de fauteuil roulant",
  },
  {
    img: "https://static.prod-images.emergentagent.com/jobs/3605812b-40ae-4438-9f86-28dfb90c74fb/images/62ed7f0fd4203e46482be0c6507c1c3dbff1432aafefe20c27d91fae0ba8d625.jpeg",
    quote: "Après 30 ans de fonderie, mon dos ne suivait plus. La location de l'exosquelette m'a permis de tester sans engagement — aujourd'hui j'en porte un tous les jours et je finirai ma carrière à l'atelier, pas en arrêt.",
    name: "Kenji Nakamura", role: "Opérateur fonderie, 54 ans",
  },
];

const STATS = [
  ["6 %", "obligation d'emploi de travailleurs handicapés (OETH) pour les entreprises de 20 salariés et plus"],
  ["2,9 M", "de personnes en âge de travailler disposent d'une reconnaissance administrative de handicap en France"],
  ["80 %", "des handicaps sont invisibles — l'aménagement du poste change tout au quotidien"],
];

const LEVIERS = [
  { icon: Wrench, t: "Aménagement de poste", d: "Sièges ergonomiques, postes assis-debout, bras d'écran, périphériques adaptés : nous équipons chaque poste selon les préconisations de la médecine du travail." },
  { icon: Accessibility, t: "Exosquelettes d'assistance", d: "Nos exosquelettes passifs réduisent l'exigence physique des postes de manutention et de travail bras levés — un levier concret de maintien et de retour à l'emploi." },
  { icon: HandCoins, t: "Financements AGEFIPH / FIPHFP", d: "Nous vous aidons à mobiliser les aides à l'aménagement des situations de travail pour financer tout ou partie des équipements." },
  { icon: GraduationCap, t: "Sensibilisation des équipes", d: "Formations et ateliers pour les managers et les collectifs de travail : accueillir, intégrer et coopérer sans stigmatiser." },
  { icon: Users, t: "Étude ergonomique du poste", d: "Nos ergonomes analysent l'activité réelle, co-construisent les solutions avec le salarié et accompagnent l'essai des équipements sur site." },
  { icon: HeartHandshake, t: "Maintien dans l'emploi", d: "Restriction d'aptitude, RQTH, retour après un arrêt long : nous construisons avec vous des solutions durables, en lien avec le médecin du travail." },
];

export default function InclusionHandicap() {
  const [exos, setExos] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    api.get("/products").then((r) => {
      setExos(r.data.filter((p) => p.subcategory === "Exosquelettes & assistance physique"));
    }).catch(() => {});
  }, []);

  return (
    <div data-testid="inclusion-page" className="pt-[148px]">
      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-16 md:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14 items-center">
        <div className="lg:col-span-6">
          <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Inclusion & handicap</p></Reveal>
          <Reveal delay={0.05}>
            <h1 className="font-display font-black text-5xl sm:text-6xl tracking-tight leading-[0.95]">Un poste adapté, un talent révélé</h1>
            <p className="mt-6 text-lg md:text-xl max-w-xl" style={{ color: "var(--ink-2)" }}>
              Nous aidons les entreprises à recruter, intégrer et maintenir en emploi les personnes en situation de handicap : étude ergonomique, équipements d'assistance, exosquelettes et mobilisation des financements.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-4">
              <Link to="/contact" data-testid="inclusion-devis-btn" className="btn-primary px-8 py-4 font-semibold flex items-center justify-center gap-2">Étudier un poste <ArrowRight size={18} /></Link>
              <Link to="/catalogue" className="btn-outline px-8 py-4 font-semibold flex items-center justify-center">Voir le matériel</Link>
            </div>
          </Reveal>
        </div>
        <div className="lg:col-span-6">
          <Reveal delay={0.12}>
            <div className="overflow-hidden" style={{ border: "1px solid var(--border)" }}>
              <img src={HERO} alt="Équipe inclusive en entrepôt avec exosquelette et poste adapté" className="w-full h-80 lg:h-[440px] object-cover" data-testid="inclusion-hero-image" />
            </div>
          </Reveal>
        </div>
      </section>

      <section style={{ background: "var(--ink)", color: "var(--bone)" }}>
        <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-14 grid grid-cols-1 md:grid-cols-3 gap-10">
          {STATS.map(([n, l], i) => (
            <Reveal key={n} delay={i * 0.08}>
              <p className="font-display font-black text-5xl" style={{ color: "var(--orange)" }}>{n}</p>
              <p className="mt-3 text-sm leading-relaxed" style={{ color: "rgba(245,245,240,0.75)" }}>{l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Nos leviers</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none max-w-3xl mb-14">Six leviers pour rendre chaque poste accessible</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {LEVIERS.map((s, i) => {
            const Icon = s.icon;
            return (
              <Reveal key={s.t} delay={(i % 3) * 0.06}>
                <div className="p-8 md:p-10 h-full" style={{ background: "var(--white)" }} data-testid={`levier-${i}`}>
                  <Icon size={32} strokeWidth={1.6} style={{ color: "var(--orange)" }} />
                  <h3 className="font-display font-bold text-xl mt-5 mb-2">{s.t}</h3>
                  <p style={{ color: "var(--ink-2)" }}>{s.d}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </section>

      {exos.length > 0 && (
        <section style={{ background: "var(--white)", borderTop: "1px solid var(--border)", borderBottom: "1px solid var(--border)" }}>
          <div className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
            <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Équipements phares</p></Reveal>
            <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none mb-12">Les exosquelettes qui abaissent l'exigence du poste</h2></Reveal>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {exos.map((p, i) => (
                <Reveal key={p.id} delay={i * 0.08}>
                  <Link to={`/catalogue/${p.id}`} className="group flex flex-col sm:flex-row gap-6 p-6 h-full" style={{ background: "var(--bone)", border: "1px solid var(--border)" }} data-testid={`inclusion-exo-${p.id}`}>
                    <div className="overflow-hidden sm:w-44 shrink-0">
                      <img src={p.image} alt={p.name} className="w-full h-44 object-cover transition-transform duration-700 group-hover:scale-105" />
                    </div>
                    <div className="flex flex-col">
                      <h3 className="font-display font-bold text-xl leading-tight">{p.name}</h3>
                      <p className="mt-3 flex-1 text-sm" style={{ color: "var(--ink-2)" }}>{p.short}</p>
                      <p className="mt-4 font-display font-black text-xl">Sur devis</p>
                    </div>
                  </Link>
                </Reveal>
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28" data-testid="inclusion-temoignages">
        <Reveal><p className="overline mb-4" style={{ color: "var(--orange)" }}>Retours d'expérience</p></Reveal>
        <Reveal delay={0.05}><h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none max-w-3xl mb-14">Ils l'ont fait — et ça marche</h2></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-px" style={{ background: "var(--border)", border: "1px solid var(--border)" }}>
          {TEMOIGNAGES.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.08}>
              <div className="h-full flex flex-col" style={{ background: "var(--white)" }} data-testid={`inclusion-temoin-${i}`}>
                <div className="overflow-hidden">
                  <img src={t.img} alt={t.name} className="w-full h-64 object-cover object-top" />
                </div>
                <div className="p-7 flex flex-col flex-1">
                  <p className="flex-1 leading-relaxed" style={{ color: "var(--ink)" }}>“{t.quote}”</p>
                  <div className="mt-6">
                    <p className="font-display font-bold">{t.name}</p>
                    <p className="text-sm" style={{ color: "var(--ink-2)" }}>{t.role}</p>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-28">
        <Reveal>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 p-10 md:p-16" style={{ background: "var(--orange)", color: "var(--bone)" }}>
            <div>
              <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tight leading-none">Parlons de votre situation</h2>
              <p className="mt-4 text-lg md:text-xl max-w-lg" style={{ color: "rgba(245,245,240,0.9)" }}>Étude de poste, essai d'exosquelette sur site, montage du dossier de financement : premier échange offert.</p>
            </div>
            <Link to="/contact" data-testid="inclusion-contact-btn" className="px-10 py-5 font-display font-bold text-lg whitespace-nowrap" style={{ background: "var(--ink)", color: "var(--bone)" }}>Nous contacter</Link>
          </div>
        </Reveal>
      </section>
    </div>
  );
}
